import { useFeatureFlags, useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Accordion from './Accordion';

import type { CompositionTemplate } from '../sidepanel/CompositionSettings/types';

const logger = LogManager.getLogger('BrandKit:PullQuotes');

const BrandKitPullQuotes = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const {
    contentStudioQuotesEnableGlobalTemplates: enableGlobalTemplates = true,
  } = useFeatureFlags();

  const [allTemplates, setAllTemplates] = useState<CompositionTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const customTemplateIds = useMemo(
    () => videoCreator.organization?.quoteCardsBranding?.map(c => c.id) || [],
    [videoCreator.organization?.quoteCardsBranding],
  );

  // Fetch quote card composition templates (custom + global presets)
  useEffect(() => {
    let cancelled = false;
    const fetchTemplates = async () => {
      setIsLoading(true);
      try {
        const options = { isQuoteCard: true };

        // Fetch global presets only when the flag allows it
        const globalTemplates = enableGlobalTemplates
          ? await datoClientStore.compositionRepository.getCompositions(options)
          : [];

        // Fetch custom "Workspace Templates" if any exist
        let customTemplates: CompositionTemplate[] = [];
        if (customTemplateIds.length > 0) {
          customTemplates =
            await datoClientStore.compositionRepository.getCompositionsByIds(
              customTemplateIds,
              options,
            );
        }

        if (!cancelled) {
          // Combine, deduplicate by id (custom first)
          const seen = new Set<string>();
          const combined: CompositionTemplate[] = [];
          for (const t of [...customTemplates, ...globalTemplates]) {
            if (!seen.has(t.id)) {
              seen.add(t.id);
              combined.push(t);
            }
          }
          setAllTemplates(combined);
        }
      } catch (error) {
        logger.error('Failed to fetch pull quote templates', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchTemplates();
    return () => {
      cancelled = true;
    };
  }, [datoClientStore, customTemplateIds, enableGlobalTemplates]);

  // Selection state — null means "No Pull Quote Template"
  const [selectedId, setSelectedId] = useState<string | null>(
    videoCreator.organization?.defaultPullQuoteTemplate?.id ?? null,
  );

  // Sync when organization loads asynchronously
  useEffect(() => {
    const orgDefault = videoCreator.organization?.defaultPullQuoteTemplate?.id;
    setSelectedId(orgDefault ?? null);
  }, [videoCreator.organization?.defaultPullQuoteTemplate?.id]);

  const handleSelect = useCallback(
    async (templateId: string | null) => {
      setSelectedId(templateId);
      setIsSaving(true);
      setIsSaved(false);

      try {
        const organization = videoCreator.organization;
        if (!organization) return;

        organization.defaultPullQuoteTemplate = templateId
          ? { id: templateId }
          : null;
        await datoClientStore.albumRepository?.update(organization);
        setIsSaved(true);
      } catch (error) {
        logger.error('Failed to save default pull quote template', error);
      } finally {
        setIsSaving(false);
      }
    },
    [videoCreator, datoClientStore],
  );

  return (
    <Accordion
      id="pull-quotes"
      title={`Pull Quotes (${allTemplates.length})`}
      isSaving={isSaving}
      isSaved={isSaved}
    >
      <Subtitle>
        The selected Pull Quote template will be used as the default for newly
        created pull quote cards
      </Subtitle>
      <TemplateContainer>
        {isLoading ? (
          <LoadingText>Loading templates...</LoadingText>
        ) : (
          <TemplateGrid>
            <CardWrapper
              isSelected={selectedId === null}
              onClick={() => handleSelect(null)}
            >
              <NoTemplateCard>No Pull Quote Template</NoTemplateCard>
            </CardWrapper>

            {allTemplates.map(template => (
              <CardWrapper
                key={template.id}
                isSelected={selectedId === template.id}
                onClick={() => handleSelect(template.id)}
              >
                <CardTitle>{template.label}</CardTitle>
                {template.thumbnail?.url ? (
                  <ThumbnailImage
                    src={template.thumbnail.url}
                    alt={template.label}
                  />
                ) : (
                  <ThumbnailPlaceholder>No Preview</ThumbnailPlaceholder>
                )}
              </CardWrapper>
            ))}
          </TemplateGrid>
        )}
      </TemplateContainer>
    </Accordion>
  );
});

const Subtitle = styled.p`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 1.5;
  margin: 0 0 16px;
`;

const TemplateContainer = styled.div`
  background: var(--surface-elevated);
  border-radius: 8px;
  padding: 24px;
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const CardWrapper = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid
    ${({ isSelected }) => (isSelected ? 'var(--primary)' : 'var(--border)')};
  background: var(--background);
  padding: 12px;
  gap: 8px;
  transition: border-color var(--transition-fast) ease;

  &:hover {
    border-color: var(--primary);
  }
`;

const NoTemplateCard = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  text-align: center;
  min-height: 120px;
`;

const CardTitle = styled.div`
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 4px;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  border-radius: 4px;
  color: var(--text-muted);
  font-size: var(--text-xs);
`;

const LoadingText = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  text-align: center;
  padding: 24px;
`;

export default BrandKitPullQuotes;
